
import * as React from 'react';
import { IEntry } from '../../utilities/fileSystem/FileEntries';
import { CancelError } from '../../utilities/async/CancelError';
import { connectToItemCache, IItemCacheDispatchProp } from '../decorators/connectToItemCache';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { IDecoratedProps } from '../../utilities/components/Decorators';
import {
  IOnItemUploadedProps,
  IOnItemQueuedProps
} from '../../operations/uploadItems/uploadEntries';
import {
  IUploaderCoreProps,
  IOnUploadStartedProps,
  IOnUploadCompletedProps,
  IOnUploadFailedProps
} from './UploaderCore';
import { asAsync } from '../../utilities/components/Async';

const UploaderCore = asAsync(async () => (await import(/* webpackChunkName: 'deferredcomponents' */'./UploaderCore')).UploaderCore);

export { IOnItemUploadedProps, IOnItemQueuedProps };

/**
 * @public
 */
export { IOnUploadStartedProps };

/**
 * @public
 */
export { IOnUploadCompletedProps };

/**
 * @public
 */
export { IOnUploadFailedProps };

/**
 * @public
 */
export interface IFileSelectorProps {
  onExecuteProps?: IOnExecuteProps;
  onContinue: (entries: IEntry[]) => void;
  onCancel: () => void;
}

export type IBaseUploaderProps = IDecoratedProps<IUploaderCoreProps, Pick<IUploaderCoreProps, 'entryBatchKey'>, {
  fileSelector: React.ComponentType<IFileSelectorProps>;
} & IItemCacheDispatchProp>;

/**
 * @internal
 */
export interface IUploaderState {
  entries: IEntry[] | undefined;
  entryBatchKey: string | undefined;
  isCanceled: boolean;
}

const UPLOADER_INITIAL_STATE: IUploaderState = {
  entries: undefined,
  entryBatchKey: undefined,
  isCanceled: false
};

/**
 * Component which manages the state machine for an Upload action.
 * @internal
 */
export class BaseUploader extends React.PureComponent<IBaseUploaderProps, IUploaderState> {
  constructor(props: IBaseUploaderProps) {
    super(props);

    this.state = UPLOADER_INITIAL_STATE;
  }

  public render(): JSX.Element | null {
    const {
      fileSelector: FileSelector,
      ...props
    } = this.props;

    const {
      entryBatchKey
    } = this.state;

    return !entryBatchKey ? (
      <FileSelector
        onExecuteProps={ this.props.onExecuteProps }
        onContinue={ this._onContinueDialog }
        onCancel={ this._onCancelDialog }
      />
    ) : (
        <UploaderCore
          { ...props }
          entryBatchKey={ entryBatchKey }
        />
      );
  }

  public componentDidMount(): void {
    this._onStateChanged(UPLOADER_INITIAL_STATE);
  }

  public componentDidUpdate(previousProps: IBaseUploaderProps, previousState: IUploaderState): void {
    this._onStateChanged(previousState);
  }

  private _onStateChanged(previousState: IUploaderState): void {
    const {
      isCanceled,
      entries
    } = this.state;

    if (!previousState.isCanceled && isCanceled) {
      this.props.reject(new CancelError());
    } else if (!previousState.entries && entries) {
      this._loadEntries(entries);
    }
  }

  private _onContinueDialog = (entries: IEntry[]): void => {
    this.setState({
      entries: entries
    });
  }

  private async _loadEntries(entries: IEntry[]): Promise<void> {
    const {
      createEntryBatch
    } = await import(/* webpackChunkName: 'deferredoperations' */'../../operations/DeferredOperations');

    const {
      itemKey: entryBatchKey
    } = this.props.itemCacheDispatch(createEntryBatch({
      entries: entries
    }));

    this.setState({
      entryBatchKey: entryBatchKey
    });
  }

  private _onCancelDialog = (): void => {
    this.setState({
      isCanceled: true
    });
  }
}

/**
 * @public
 */
export type IUploaderProps = IDecoratedProps<IBaseUploaderProps, IItemCacheDispatchProp, {}>;

/**
 * @public
 */
export const Uploader: React.ComponentType<IUploaderProps> = connectToItemCache()(BaseUploader);
